import * as THREE from "three";
import type { Artwork, ArtworkWrapper } from "@/types/artworksData";
import AppData from "../../app.data";
import {
  PLACEHOLDER_IMPORT_MESSAGE,
  descriptionParagraphs,
  getImportedMetadataDisplayRows,
  isEuropeanaImportSource,
  isPlaceholderImport,
  normalizePseudoMoMDisplayYear,
  resolveEuropeanaImportSourceDisplay,
  resolveEuropeanaProviderDisplay,
} from "../../../artworks/importedMetadata";

type VRMetadataPanelAction = "close";

export type VRMetadataPanelHit = {
  action?: VRMetadataPanelAction;
  object: THREE.Object3D;
  point?: THREE.Vector3;
  distance?: number;
};

const PANEL_DISTANCE = 1.25;
const PANEL_WIDTH = 1.14;
const PANEL_HEIGHT = 0.74;
const TEXTURE_WIDTH = 1024;
const TEXTURE_HEIGHT = 896;
const PANEL_PADDING_X = 40;
const PANEL_COLUMN_GAP = 36;
const PANEL_CONTENT_TOP = 150;
const PANEL_CONTENT_BOTTOM = TEXTURE_HEIGHT - 48;
const PANEL_COLUMN_WIDTH = (TEXTURE_WIDTH - PANEL_PADDING_X * 2 - PANEL_COLUMN_GAP) / 2;
const PANEL_TITLE_WIDTH = TEXTURE_WIDTH - PANEL_PADDING_X * 2 - 112;
const PANEL_ROW_GAP = 18;
const PANEL_LABEL_LINE_HEIGHT = 28;
const PANEL_VALUE_LINE_HEIGHT = 31;

const appData = new AppData();

type MetadataRow = {
  label: string;
  value: string;
  multiline?: boolean;
};

const disposeTexture = (texture: THREE.Texture | null | undefined) => {
  texture?.dispose?.();
};

const disposeMaterial = (material: THREE.Material | THREE.Material[] | null | undefined) => {
  if (!material) return;
  if (Array.isArray(material)) {
    material.forEach((entry) => entry?.dispose?.());
    return;
  }
  material.dispose?.();
};

const textValue = (value: unknown): string => {
  if (Array.isArray(value)) return value.filter(Boolean).join(" ");
  if (typeof value === "string") return value;
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (value == null) return "";
  return String(value);
};

const cleanText = (value: unknown): string => {
  return textValue(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const splitLongWord = (
  context: CanvasRenderingContext2D,
  word: string,
  maxWidth: number
) => {
  if (context.measureText(word).width <= maxWidth) return [word];

  const chunks: string[] = [];
  let chunk = "";

  Array.from(word).forEach((char) => {
    const candidate = `${chunk}${char}`;
    if (chunk && context.measureText(candidate).width > maxWidth) {
      chunks.push(chunk);
      chunk = char;
      return;
    }
    chunk = candidate;
  });

  if (chunk) chunks.push(chunk);
  return chunks;
};

const getActiveArtwork = (wrapper: ArtworkWrapper): Artwork | null => {
  const index = Number.isFinite(Number(wrapper.activeArtwork)) ? Number(wrapper.activeArtwork) : 0;
  return wrapper.artworks?.[index] || null;
};

const getArtworkProvider = (artwork: Artwork) => {
  return cleanText(
    resolveEuropeanaProviderDisplay(artwork.source) ||
    artwork.edmProfile?.dataProvider ||
    artwork.source?.provider ||
    artwork.source?.sourceName
  );
};

const getArtworkDate = (artwork: Artwork) => {
  if (artwork.source?.sourceName?.toLowerCase() === "europeana") {
    return cleanText(normalizePseudoMoMDisplayYear((artwork.source as any).year, artwork.edmProfile?.contextEntities));
  }
  return artwork.createdAt ? cleanText(new Date(artwork.createdAt).getFullYear()) : "";
};

const formatDate = (value: unknown) => {
  if (!value) return "";
  const date = new Date(value as any);
  return Number.isNaN(date.getTime()) ? cleanText(value) : date.toLocaleDateString();
};

const createMetadataRows = (artwork: Artwork): MetadataRow[] => {
  const usePseudoMoMMetadata = appData.spaceData?.subtype === "pseudoMoM";
  const rows: MetadataRow[] = [];
  const provider = getArtworkProvider(artwork);
  const year = getArtworkDate(artwork);

  if (artwork.artistName) {
    rows.push({ label: usePseudoMoMMetadata ? "Creator" : "Artist", value: cleanText(artwork.artistName) });
  }
  if (artwork.title) {
    rows.push({ label: "Title", value: cleanText(artwork.title) });
  }
  if (year) {
    rows.push({ label: "Year", value: year });
  }
  const description = descriptionParagraphs(artwork.description).join("\n\n");
  if (description) {
    rows.push({ label: "Description", value: cleanText(description), multiline: true });
  }
  if (artwork.source && provider) {
    rows.push({ label: "Provider", value: provider });
  }
  if (artwork.source && usePseudoMoMMetadata && isEuropeanaImportSource(artwork.source)) {
    rows.push({ label: "Import source", value: resolveEuropeanaImportSourceDisplay(artwork.source) });
  }
  if (
    artwork.source &&
    !usePseudoMoMMetadata &&
    (artwork.source.sourceName?.toLowerCase() === "europeana" || artwork.source.provider?.toLowerCase() === "europeana") &&
    artwork.source.edmConceptPrefLabelLangAware?.length
  ) {
    rows.push({ label: "Labels", value: artwork.source.edmConceptPrefLabelLangAware.join(", "), multiline: true });
  }
  if (isPlaceholderImport(artwork.source) && !usePseudoMoMMetadata) {
    rows.push({ label: "Media status", value: PLACEHOLDER_IMPORT_MESSAGE, multiline: true });
  }

  getImportedMetadataDisplayRows({
    source: artwork.source,
    artworkTitle: artwork.title,
    artworkDescription: artwork.description,
    artworkYear: year,
    pseudoMoMReadMode: usePseudoMoMMetadata,
    contextEntities: artwork.edmProfile?.contextEntities,
    artworkTags: artwork.tags,
  }).forEach((row) => {
    rows.push({ label: row.label, value: row.value, multiline: row.value.includes("\n") });
  });

  if (artwork.physical && artwork.physicalMeta) {
    if (artwork.physicalMeta.format) rows.push({ label: "Format", value: cleanText(artwork.physicalMeta.format) });
    if (artwork.physicalMeta.materials) rows.push({ label: "Materials", value: cleanText(artwork.physicalMeta.materials) });
    if (artwork.physicalMeta.dateOfCreation) {
      rows.push({ label: "Date of Creation", value: formatDate(artwork.physicalMeta.dateOfCreation) });
    }
  }

  const rights = artwork.source?.rights?.find((value) => cleanText(value));
  if (rights) {
    rows.push({ label: "Rights statement", value: cleanText(rights), multiline: true });
  }
  if (artwork.source?.sourceName === "europeana" && artwork.source.itemUrl) {
    rows.push({ label: "Listing source", value: cleanText(artwork.source.itemUrl), multiline: true });
  }
  if (artwork.physical && artwork.attachments?.length) {
    rows.push({
      label: "Authenticity documents",
      value: artwork.attachments.map((attachment) => cleanText(attachment.name)).filter(Boolean).join("\n"),
      multiline: true,
    });
  }

  return rows;
};

const wrapText = (
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines = Number.POSITIVE_INFINITY
) => {
  const words = cleanText(text).split(" ").filter(Boolean);
  const lines: string[] = [];
  let line = "";

  words.forEach((word) => {
    const candidate = line ? `${line} ${word}` : word;
    if (context.measureText(candidate).width <= maxWidth) {
      line = candidate;
      return;
    }
    if (line) {
      lines.push(line);
      line = "";
    }

    const wordLines = splitLongWord(context, word, maxWidth);
    if (wordLines.length > 1) {
      lines.push(...wordLines.slice(0, -1));
      line = wordLines[wordLines.length - 1] || "";
      return;
    }
    line = word;
  });

  if (line) lines.push(line);
  if (Number.isFinite(maxLines) && lines.length > maxLines) {
    const clipped = lines.slice(0, maxLines);
    clipped[maxLines - 1] = `${clipped[maxLines - 1].replace(/\s+$/, "")}...`;
    return clipped;
  }
  return lines;
};

const drawWrappedText = (
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  maxLines = Number.POSITIVE_INFINITY
) => {
  const lines = wrapText(context, text, maxWidth, maxLines);
  lines.forEach((line, index) => {
    context.fillText(line, x, y + index * lineHeight);
  });
  return y + Math.max(lines.length, 1) * lineHeight;
};

const measureMetadataRowHeight = (
  context: CanvasRenderingContext2D,
  row: MetadataRow,
  maxWidth: number
) => {
  context.font = "700 22px Arial, sans-serif";
  const labelLines = wrapText(context, row.label, maxWidth, 1);
  context.font = "400 25px Arial, sans-serif";
  const valueLines = wrapText(context, row.value, maxWidth);
  return (
    Math.max(labelLines.length, 1) * PANEL_LABEL_LINE_HEIGHT +
    4 +
    Math.max(valueLines.length, 1) * PANEL_VALUE_LINE_HEIGHT
  );
};

const drawMetadataRow = (
  context: CanvasRenderingContext2D,
  row: MetadataRow,
  x: number,
  y: number,
  maxWidth: number
) => {
  context.font = "700 22px Arial, sans-serif";
  context.fillStyle = "#A7F3D0";
  let nextY = drawWrappedText(context, row.label, x, y, maxWidth, PANEL_LABEL_LINE_HEIGHT, 1);
  context.font = "400 25px Arial, sans-serif";
  context.fillStyle = "#E2E8F0";
  nextY = drawWrappedText(context, row.value, x, nextY + 4, maxWidth, PANEL_VALUE_LINE_HEIGHT);
  return nextY;
};

const drawMetadataRows = (
  context: CanvasRenderingContext2D,
  rows: MetadataRow[],
  scrollOffset: number
) => {
  const leftX = PANEL_PADDING_X;
  const rightX = PANEL_PADDING_X + PANEL_COLUMN_WIDTH + PANEL_COLUMN_GAP;
  const contentHeight = PANEL_CONTENT_BOTTOM - PANEL_CONTENT_TOP;
  let y = PANEL_CONTENT_TOP - scrollOffset;
  let totalHeight = 0;

  context.save?.();
  context.beginPath?.();
  context.rect?.(0, PANEL_CONTENT_TOP - 16, TEXTURE_WIDTH, contentHeight + 16);
  context.clip?.();

  for (let index = 0; index < rows.length; index += 2) {
    const leftRow = rows[index];
    const rightRow = rows[index + 1];
    const leftHeight = leftRow ? measureMetadataRowHeight(context, leftRow, PANEL_COLUMN_WIDTH) : 0;
    const rightHeight = rightRow ? measureMetadataRowHeight(context, rightRow, PANEL_COLUMN_WIDTH) : 0;
    const pairHeight = Math.max(leftHeight, rightHeight);

    if (y + pairHeight >= PANEL_CONTENT_TOP - 24 && y <= PANEL_CONTENT_BOTTOM + 24) {
      if (leftRow) {
        drawMetadataRow(context, leftRow, leftX, y, PANEL_COLUMN_WIDTH);
      }
      if (rightRow) {
        drawMetadataRow(context, rightRow, rightX, y, PANEL_COLUMN_WIDTH);
      }
    }

    y += pairHeight + PANEL_ROW_GAP;
    totalHeight += pairHeight + PANEL_ROW_GAP;
  }

  context.restore?.();
  return Math.max(0, totalHeight - contentHeight);
};

const createPanelTexture = (artwork: Artwork, scrollOffset = 0) => {
  const canvas = document.createElement("canvas");
  canvas.width = TEXTURE_WIDTH;
  canvas.height = TEXTURE_HEIGHT;
  const context = canvas.getContext("2d");
  if (!context) {
    return { texture: null, maxScrollOffset: 0 };
  }

  context.fillStyle = "rgba(18, 24, 32, 0.94)";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.strokeStyle = "rgba(148, 163, 184, 0.85)";
  context.lineWidth = 4;
  context.strokeRect(2, 2, canvas.width - 4, canvas.height - 4);

  const rows = createMetadataRows(artwork);
  const title = cleanText(artwork.title) || "Untitled artwork";
  context.fillStyle = "#F8FAFC";
  context.font = "700 42px Arial, sans-serif";
  drawWrappedText(context, title, PANEL_PADDING_X, 72, PANEL_TITLE_WIDTH, 48, 2);

  const maxScrollOffset = drawMetadataRows(
    context,
    rows.filter((row) => row.label !== "Title"),
    scrollOffset
  );

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return { texture, maxScrollOffset };
};

const createCloseTexture = () => {
  const canvas = document.createElement("canvas");
  canvas.width = 128;
  canvas.height = 128;
  const context = canvas.getContext("2d");
  if (!context) return null;

  context.fillStyle = "rgba(15, 23, 42, 0.96)";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.strokeStyle = "#E2E8F0";
  context.lineWidth = 10;
  context.beginPath();
  context.moveTo(40, 40);
  context.lineTo(88, 88);
  context.moveTo(88, 40);
  context.lineTo(40, 88);
  context.stroke();

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
};

export class VRMetadataPanelManager {
  private readonly scene: THREE.Scene;
  private readonly camera: THREE.Camera;
  private root: THREE.Group | null = null;
  private panelMesh: THREE.Mesh | null = null;
  private closeButton: THREE.Mesh | null = null;
  private currentArtwork: Artwork | null = null;
  private scrollOffset = 0;
  private maxScrollOffset = 0;

  constructor(scene: THREE.Scene, camera: THREE.Camera) {
    this.scene = scene;
    this.camera = camera;
  }

  public open(wrapper: ArtworkWrapper) {
    const artwork = getActiveArtwork(wrapper);
    if (!artwork) return;

    this.close();
    this.currentArtwork = artwork;
    this.scrollOffset = 0;

    this.root = new THREE.Group();
    this.root.name = "VRMetadataPanelRoot";
    this.positionRootInFrontOfCamera();

    const { texture: panelTexture, maxScrollOffset } = createPanelTexture(artwork, this.scrollOffset);
    this.maxScrollOffset = maxScrollOffset;
    const panelMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      map: panelTexture || undefined,
      transparent: true,
      depthTest: false,
    });
    this.panelMesh = new THREE.Mesh(new THREE.PlaneGeometry(PANEL_WIDTH, PANEL_HEIGHT), panelMaterial);
    this.panelMesh.name = "VRMetadataPanel";
    this.panelMesh.renderOrder = 1000;
    this.root.add(this.panelMesh);

    const closeTexture = createCloseTexture();
    const closeMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      map: closeTexture || undefined,
      transparent: true,
      depthTest: false,
    });
    this.closeButton = new THREE.Mesh(new THREE.PlaneGeometry(0.08, 0.08), closeMaterial);
    this.closeButton.name = "VRMetadataPanelCloseButton";
    this.closeButton.userData.vrMetadataPanelAction = "close";
    this.closeButton.position.set(PANEL_WIDTH / 2 - 0.07, PANEL_HEIGHT / 2 - 0.07, 0.01);
    this.closeButton.renderOrder = 1001;
    this.root.add(this.closeButton);

    this.scene.add(this.root);
  }

  public isOpen() {
    return Boolean(this.root && this.panelMesh && this.currentArtwork);
  }

  public scrollBy(delta: number) {
    if (!this.currentArtwork || !this.panelMesh) return false;

    const nextOffset = Math.max(0, Math.min(this.maxScrollOffset, this.scrollOffset + delta));
    if (Math.abs(nextOffset - this.scrollOffset) < 0.5) return false;

    this.scrollOffset = nextOffset;
    const { texture, maxScrollOffset } = createPanelTexture(this.currentArtwork, this.scrollOffset);
    this.maxScrollOffset = maxScrollOffset;
    const material = this.panelMesh.material as THREE.MeshBasicMaterial;
    disposeTexture(material.map || null);
    material.map = texture || null;
    material.needsUpdate = true;
    return true;
  }

  public close() {
    if (!this.root) return;

    [this.panelMesh, this.closeButton].forEach((mesh) => {
      if (!mesh) return;
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      materials.forEach((material: any) => {
        disposeTexture(material?.map || null);
        disposeMaterial(material);
      });
      mesh.geometry.dispose?.();
    });

    this.scene.remove(this.root);
    this.root.clear();
    this.root = null;
    this.panelMesh = null;
    this.closeButton = null;
    this.currentArtwork = null;
    this.scrollOffset = 0;
    this.maxScrollOffset = 0;
  }

  public raycast(origin: THREE.Vector3, direction: THREE.Vector3): VRMetadataPanelHit | null {
    if (!this.root || !this.closeButton) return null;

    const raycaster = new THREE.Raycaster(origin, direction.clone().normalize());
    const hit = raycaster.intersectObject(this.closeButton, true)[0];
    if (!hit?.object) return null;

    return {
      action: "close",
      object: hit.object,
      point: hit.point?.clone(),
      distance: hit.distance,
    };
  }

  private positionRootInFrontOfCamera() {
    if (!this.root) return;

    const cameraPosition = new THREE.Vector3();
    const forward = new THREE.Vector3();
    this.camera.getWorldPosition(cameraPosition);
    this.camera.getWorldDirection(forward);
    const targetPosition = cameraPosition.clone().add(forward.multiplyScalar(PANEL_DISTANCE));
    this.root.position.copy(targetPosition);
    this.root.lookAt(cameraPosition);
  }
}
